import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Stack, CircularProgress, Snackbar, Alert, IconButton, Tooltip,
} from '@mui/material';
import { Add, Refresh, ReceiptLong } from '@mui/icons-material';
import PlatformBankService from '../../services/platformBank.service';

const ACCOUNT_TYPES = ['NGENIUS_SETTLEMENT', 'OPERATIONS', 'CASH'];
const EMPTY = { bank_name: '', account_name: '', account_number: '', iban: '', swift_code: '', account_type: 'NGENIUS_SETTLEMENT' };

const PlatformBanks = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // create dialog
  const [saving, setSaving] = useState(false);
  const [txFor, setTxFor] = useState(null); // { bank, page }
  const [txRows, setTxRows] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(() => {
    setLoading(true);
    PlatformBankService.list()
      .then((list) => setBanks(Array.isArray(list) ? list : []))
      .catch(() => notify('Failed to load banks', 'error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = () => {
    setSaving(true);
    PlatformBankService.create(form)
      .then(() => { notify('Bank account created'); setForm(null); load(); })
      .catch((e) => notify(e?.response?.data?.message || 'Create failed', 'error'))
      .finally(() => setSaving(false));
  };

  const toggleActive = (bank) => {
    PlatformBankService.setActive(bank.id, !bank.is_active)
      .then(() => { notify('Updated'); load(); })
      .catch(() => notify('Update failed', 'error'));
  };

  const openTx = (bank) => {
    setTxFor({ bank });
    PlatformBankService.getTransactions(bank.id, { page: 0, size: 50 })
      .then((page) => setTxRows(page?.content || []))
      .catch(() => notify('Failed to load transactions', 'error'));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Bank Management</Typography>
          <Typography variant="caption" color="text.secondary">Platform settlement accounts. The active NGENIUS_SETTLEMENT account receives subscription payments.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setForm({ ...EMPTY })}>Add bank</Button>
        </Stack>
      </Stack>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Bank</TableCell><TableCell>Account</TableCell><TableCell>IBAN</TableCell>
                  <TableCell>Type</TableCell><TableCell align="right">Balance</TableCell>
                  <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {banks.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No platform bank accounts</Typography></TableCell></TableRow>
                ) : banks.map((b) => (
                  <TableRow key={b.id} hover>
                    <TableCell>{b.bank_name}</TableCell>
                    <TableCell>{b.account_name}</TableCell>
                    <TableCell>{b.iban}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={b.account_type} /></TableCell>
                    <TableCell align="right">{PlatformBankService.formatMoney(b.balance)}</TableCell>
                    <TableCell><Chip size="small" label={b.is_active ? 'Active' : 'Inactive'} color={b.is_active ? 'success' : 'default'} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Transactions"><IconButton size="small" onClick={() => openTx(b)}><ReceiptLong fontSize="small" /></IconButton></Tooltip>
                      <Button size="small" onClick={() => toggleActive(b)}>{b.is_active ? 'Deactivate' : 'Activate'}</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={!!form} onClose={() => !saving && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add platform bank account</DialogTitle>
        <DialogContent>
          {form && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Bank name" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
              <TextField label="Account name" value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} />
              <TextField label="Account number" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} />
              <TextField label="IBAN" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} />
              <TextField label="SWIFT (optional)" value={form.swift_code} onChange={(e) => setForm({ ...form, swift_code: e.target.value })} />
              <TextField select label="Type" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
                {ACCOUNT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Transactions dialog */}
      <Dialog open={!!txFor} onClose={() => { setTxFor(null); setTxRows([]); }} maxWidth="md" fullWidth>
        <DialogTitle>{txFor?.bank?.bank_name} — transactions</DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell><TableCell>Ref</TableCell><TableCell>Direction</TableCell>
                  <TableCell align="right">Amount</TableCell><TableCell align="right">Balance after</TableCell><TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {txRows.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center"><Typography variant="caption" color="text.secondary">No transactions</Typography></TableCell></TableRow>
                ) : txRows.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.created_at ? String(t.created_at).slice(0, 10) : ''}</TableCell>
                    <TableCell>{t.transaction_number}</TableCell>
                    <TableCell>{t.direction}</TableCell>
                    <TableCell align="right">{PlatformBankService.formatMoney(t.amount)}</TableCell>
                    <TableCell align="right">{PlatformBankService.formatMoney(t.balance_after)}</TableCell>
                    <TableCell><Chip size="small" label={t.status} color={t.status === 'COMPLETED' ? 'success' : t.status === 'PENDING' ? 'warning' : 'default'} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions><Button onClick={() => { setTxFor(null); setTxRows([]); }}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PlatformBanks;
